/**
 * Derive an agent's display detail (name / description / tools / skills / body)
 * from the canonical item row. Shared by the agent drill-in page and the prefetch
 * so the parsing lives in ONE place (it used to be copy-pasted in both, which
 * is exactly the per-page drift that lets one copy fix and the other rot).
 *
 * The row's structured fields are the source of truth under the frontmatter
 * contract; parsing inline frontmatter out of `content` is only a fallback for
 * rows minted before the contract normalized them.
 */

import type { AgentDetailCache } from "./cache";
import { parseFrontmatter } from "./parseFrontmatter";

export interface AgentDetailSource {
  name?: string | null;
  description?: string | null;
  tools?: string[] | null;
  skills?: string[] | null;
  content?: string | null;
}

/** A slug read as a NAME — title case, for an agent with no name of its own. */
function slugToTitle(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function stripFrontmatter(raw: string): string {
  const m = raw.match(/^---\n[\s\S]*?\n---\n?([\s\S]*)$/);
  return m ? m[1].trimStart() : raw;
}

function splitList(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(/[,\n]/)
    .map((s) => s.trim().replace(/^[-*]\s*/, ""))
    .filter(Boolean);
}

export function parseAgentDetail(
  agentSlug: string,
  item: AgentDetailSource | null | undefined
): AgentDetailCache {
  const content = item?.content ?? null;
  const fm = content ? parseFrontmatter(content) : {};
  return {
    name: item?.name || fm.name || slugToTitle(agentSlug),
    description: item?.description || fm.description || "",
    tools:
      item?.tools && item.tools.length > 0 ? item.tools : splitList(fm.tools),
    // Same precedence as tools: the row's field is canon, the frontmatter is
    // the fallback for rows minted before the contract, and neither means the
    // agent carries no skills — not that we failed to find them.
    skills:
      item?.skills && item.skills.length > 0
        ? item.skills
        : splitList(fm.skills),
    body: content ? stripFrontmatter(content) : null,
  };
}
